import React, { useState, useCallback, useContext, useMemo, createContext } from 'react';
import { element, object } from 'prop-types';
import * as userSavedProjects from '../../../../shared/api/userSavedProjects';
import { raiseToast } from '../../../../shared/utils/raiseToast';
import { useChooseVariationContext } from '../Context';

const useHandleFavorites = (initialSavedId) => {
    const [savedId, setSavedId] = useState(initialSavedId);
    const { projectId, briefId, brandName, slogan, selectedVariation } = useChooseVariationContext();

    const addToFavorites = useCallback(async () => {
        try {
            const id = await userSavedProjects.create({
                projectId,
                briefId,
                brandName,
                slogan,
                variationId: selectedVariation.id,
            });
            setSavedId(id);
        } catch {
            raiseToast();
        }
    }, [brandName, briefId, projectId, selectedVariation, slogan]);

    const removeFromFavorites = useCallback(async () => {
        try {
            if (!savedId) {
                return;
            }

            await userSavedProjects.delete(savedId);
            setSavedId(null);
        } catch {
            raiseToast();
        }
    }, [savedId]);

    const toggleFavorite = useCallback(async () => {
        if (savedId) {
            await removeFromFavorites();
            return;
        }

        await addToFavorites();
    }, [savedId, addToFavorites, removeFromFavorites]);

    const contextValue = useMemo(
        () => ({
            addToFavorites,
            removeFromFavorites,
            toggleFavorite,
            isFavorite: !!savedId,
        }),
        [addToFavorites, removeFromFavorites, toggleFavorite, savedId]
    );

    return contextValue;
};

const FavoritesContext = createContext({});

export const useFavoritesContext = () => useContext(FavoritesContext);

export const FavoritesContextProvider = ({ children, value: userSavedProject }) => {
    const contextValue = useHandleFavorites(userSavedProject?.id);
    return <FavoritesContext.Provider value={contextValue}>{children}</FavoritesContext.Provider>;
};

FavoritesContextProvider.propTypes = {
    children: element,
    value: object,
};
